/**
 * Color palette configuration for MyMaintenances.
 * Each palette defines tokens consumed by Ant Design's ConfigProvider
 * as well as CSS custom-properties applied to :root / body.
 */

export interface ThemePalette {
  /** Unique key persisted in localStorage */
  key: string;
  /** i18n key for the label shown in the UI */
  labelKey: string;

  /* ── Ant Design tokens ── */
  algorithm: 'dark' | 'light';
  colorPrimary: string;
  borderRadius: number;

  /* ── Layout / component overrides (Ant Design) ── */
  siderBg: string;
  headerBg: string;
  bodyBg: string;
  menuDarkItemBg: string;
  menuDarkSubMenuItemBg: string;

  /* ── CSS custom-properties ── */
  /** Main background of the app */
  bgBase: string;
  /** Slightly lighter surface (cards, sidebar) */
  bgSurface: string;
  /** Elevated surface (modals, popovers) */
  bgElevated: string;
  /** Primary text color */
  textPrimary: string;
  /** Secondary / muted text */
  textSecondary: string;
  /** Faint text (placeholders, hints) */
  textTertiary: string;
  /** Main border color */
  borderColor: string;
  /** Subtle border */
  borderColorLight: string;
  /** Scrollbar thumb */
  scrollbarThumb: string;
  scrollbarThumbHover: string;
  /** Login gradient stops */
  loginGradientStart: string;
  loginGradientMid: string;
  loginGradientEnd: string;
  /** Login card bg */
  loginCardBg: string;
  /** Tree hover / selection */
  treeHoverBg: string;
  treeSelectedBg: string;
  /** Explorer item states */
  explorerItemHoverBg: string;
  explorerItemActiveBg: string;
  explorerItemActiveBorder: string;
  /** Calendar today bg */
  calendarTodayBg: string;
  /** Calendar selected bg */
  calendarSelectedBg: string;
  /** Overdue / upcoming colors */
  colorOverdue: string;
  colorUpcoming: string;
  colorOk: string;
  /** Tag: node label bg & border */
  nodeLabelBg: string;
  nodeLabelBorder: string;
  nodeLabelColor: string;
}

export const darkPalette: ThemePalette = {
  key: 'dark',
  labelKey: 'profile.themeDark',
  algorithm: 'dark',
  colorPrimary: '#1677ff',
  borderRadius: 8,

  siderBg: '#141414',
  headerBg: '#141414',
  bodyBg: '#0a0a0a',
  menuDarkItemBg: '#141414',
  menuDarkSubMenuItemBg: '#141414',

  bgBase: '#0a0a0a',
  bgSurface: '#141414',
  bgElevated: 'rgba(20, 20, 20, 0.95)',
  textPrimary: '#e0e0e0',
  textSecondary: '#888',
  textTertiary: '#555',
  borderColor: '#222',
  borderColorLight: '#1f1f1f',
  scrollbarThumb: '#333',
  scrollbarThumbHover: '#555',

  loginGradientStart: '#0a0a0a',
  loginGradientMid: '#1a1a2e',
  loginGradientEnd: '#16213e',
  loginCardBg: 'rgba(20, 20, 20, 0.95)',

  treeHoverBg: '#1a1a1a',
  treeSelectedBg: '#1a2540',

  explorerItemHoverBg: '#1a1a1a',
  explorerItemActiveBg: '#1a2332',
  explorerItemActiveBorder: '#1677ff',

  calendarTodayBg: '#1a2332',
  calendarSelectedBg: '#1677ff',

  colorOverdue: '#ff4d4f',
  colorUpcoming: '#faad14',
  colorOk: '#52c41a',

  nodeLabelBg: '#1a1a1a',
  nodeLabelBorder: '#333',
  nodeLabelColor: '#555',
};

export const lightPalette: ThemePalette = {
  key: 'light',
  labelKey: 'profile.themeLight',
  algorithm: 'light',
  colorPrimary: '#1677ff',
  borderRadius: 8,

  siderBg: '#ffffff',
  headerBg: '#ffffff',
  bodyBg: '#f5f5f5',
  menuDarkItemBg: '#ffffff',
  menuDarkSubMenuItemBg: '#ffffff',

  bgBase: '#f5f5f5',
  bgSurface: '#ffffff',
  bgElevated: '#ffffff',
  textPrimary: '#1a1a1a',
  textSecondary: '#666',
  textTertiary: '#999',
  borderColor: '#e0e0e0',
  borderColorLight: '#ebebeb',
  scrollbarThumb: '#ccc',
  scrollbarThumbHover: '#aaa',

  loginGradientStart: '#e8eaf6',
  loginGradientMid: '#c5cae9',
  loginGradientEnd: '#9fa8da',
  loginCardBg: 'rgba(255, 255, 255, 0.95)',

  treeHoverBg: '#f0f0f0',
  treeSelectedBg: '#e6f4ff',

  explorerItemHoverBg: '#f0f0f0',
  explorerItemActiveBg: '#e6f4ff',
  explorerItemActiveBorder: '#1677ff',

  calendarTodayBg: '#e6f4ff',
  calendarSelectedBg: '#1677ff',

  colorOverdue: '#ff4d4f',
  colorUpcoming: '#faad14',
  colorOk: '#52c41a',

  nodeLabelBg: '#f0f0f0',
  nodeLabelBorder: '#d9d9d9',
  nodeLabelColor: '#888',
};

export const navyPalette: ThemePalette = {
  key: 'navy',
  labelKey: 'profile.themeNavy',
  algorithm: 'dark',
  colorPrimary: '#3b82f6',
  borderRadius: 8,

  siderBg: '#0d1b2a',
  headerBg: '#0d1b2a',
  bodyBg: '#020c18',
  menuDarkItemBg: '#0d1b2a',
  menuDarkSubMenuItemBg: '#0d1b2a',

  bgBase: '#020c18',
  bgSurface: '#0d1b2a',
  bgElevated: 'rgba(13, 27, 42, 0.95)',
  textPrimary: '#e2e8f0',
  textSecondary: '#94a3b8',
  textTertiary: '#475569',
  borderColor: '#1e3a5f',
  borderColorLight: '#162d4a',
  scrollbarThumb: '#1e3a5f',
  scrollbarThumbHover: '#2d5a8e',

  loginGradientStart: '#020c18',
  loginGradientMid: '#0d1b2a',
  loginGradientEnd: '#0f3460',
  loginCardBg: 'rgba(13, 27, 42, 0.95)',

  treeHoverBg: '#0f2340',
  treeSelectedBg: '#1a3a6b',

  explorerItemHoverBg: '#0f2340',
  explorerItemActiveBg: '#1a3a6b',
  explorerItemActiveBorder: '#3b82f6',

  calendarTodayBg: '#1a3a6b',
  calendarSelectedBg: '#3b82f6',

  colorOverdue: '#f87171',
  colorUpcoming: '#fbbf24',
  colorOk: '#4ade80',

  nodeLabelBg: '#0d1b2a',
  nodeLabelBorder: '#1e3a5f',
  nodeLabelColor: '#475569',
};

export const forestPalette: ThemePalette = {
  key: 'forest',
  labelKey: 'profile.themeForest',
  algorithm: 'dark',
  colorPrimary: '#22c55e',
  borderRadius: 8,

  siderBg: '#0f1a10',
  headerBg: '#0f1a10',
  bodyBg: '#060d06',
  menuDarkItemBg: '#0f1a10',
  menuDarkSubMenuItemBg: '#0f1a10',

  bgBase: '#060d06',
  bgSurface: '#0f1a10',
  bgElevated: 'rgba(15, 26, 16, 0.95)',
  textPrimary: '#e2f0e4',
  textSecondary: '#86a98a',
  textTertiary: '#4a6b4d',
  borderColor: '#1a3d1c',
  borderColorLight: '#152f17',
  scrollbarThumb: '#1a3d1c',
  scrollbarThumbHover: '#2a5c2d',

  loginGradientStart: '#060d06',
  loginGradientMid: '#0f1a10',
  loginGradientEnd: '#0d2e10',
  loginCardBg: 'rgba(15, 26, 16, 0.95)',

  treeHoverBg: '#102612',
  treeSelectedBg: '#1a4a1d',

  explorerItemHoverBg: '#102612',
  explorerItemActiveBg: '#1a4a1d',
  explorerItemActiveBorder: '#22c55e',

  calendarTodayBg: '#1a4a1d',
  calendarSelectedBg: '#22c55e',

  colorOverdue: '#f87171',
  colorUpcoming: '#fbbf24',
  colorOk: '#22c55e',

  nodeLabelBg: '#0f1a10',
  nodeLabelBorder: '#1a3d1c',
  nodeLabelColor: '#4a6b4d',
};

export const amberPalette: ThemePalette = {
  key: 'amber',
  labelKey: 'profile.themeAmber',
  algorithm: 'dark',
  colorPrimary: '#f59e0b',
  borderRadius: 8,

  siderBg: '#1a1200',
  headerBg: '#1a1200',
  bodyBg: '#0d0900',
  menuDarkItemBg: '#1a1200',
  menuDarkSubMenuItemBg: '#1a1200',

  bgBase: '#0d0900',
  bgSurface: '#1a1200',
  bgElevated: 'rgba(26, 18, 0, 0.95)',
  textPrimary: '#fef3c7',
  textSecondary: '#d97706',
  textTertiary: '#78350f',
  borderColor: '#3d2a00',
  borderColorLight: '#2e2000',
  scrollbarThumb: '#3d2a00',
  scrollbarThumbHover: '#5a3e00',

  loginGradientStart: '#0d0900',
  loginGradientMid: '#1a1200',
  loginGradientEnd: '#2d1f00',
  loginCardBg: 'rgba(26, 18, 0, 0.95)',

  treeHoverBg: '#201600',
  treeSelectedBg: '#3d2a00',

  explorerItemHoverBg: '#201600',
  explorerItemActiveBg: '#3d2a00',
  explorerItemActiveBorder: '#f59e0b',

  calendarTodayBg: '#3d2a00',
  calendarSelectedBg: '#f59e0b',

  colorOverdue: '#ef4444',
  colorUpcoming: '#f59e0b',
  colorOk: '#22c55e',

  nodeLabelBg: '#1a1200',
  nodeLabelBorder: '#3d2a00',
  nodeLabelColor: '#78350f',
};

export const lavenderPalette: ThemePalette = {
  key: 'lavender',
  labelKey: 'profile.themeLavender',
  algorithm: 'light',
  colorPrimary: '#7c3aed',
  borderRadius: 8,

  siderBg: '#f5f3ff',
  headerBg: '#f5f3ff',
  bodyBg: '#ede9fe',
  menuDarkItemBg: '#f5f3ff',
  menuDarkSubMenuItemBg: '#f5f3ff',

  bgBase: '#ede9fe',
  bgSurface: '#f5f3ff',
  bgElevated: '#ffffff',
  textPrimary: '#1e1b4b',
  textSecondary: '#5b21b6',
  textTertiary: '#a78bfa',
  borderColor: '#c4b5fd',
  borderColorLight: '#ddd6fe',
  scrollbarThumb: '#c4b5fd',
  scrollbarThumbHover: '#a78bfa',

  loginGradientStart: '#ede9fe',
  loginGradientMid: '#ddd6fe',
  loginGradientEnd: '#c4b5fd',
  loginCardBg: 'rgba(255, 255, 255, 0.95)',

  treeHoverBg: '#ede9fe',
  treeSelectedBg: '#ddd6fe',

  explorerItemHoverBg: '#ede9fe',
  explorerItemActiveBg: '#ddd6fe',
  explorerItemActiveBorder: '#7c3aed',

  calendarTodayBg: '#ddd6fe',
  calendarSelectedBg: '#7c3aed',

  colorOverdue: '#dc2626',
  colorUpcoming: '#d97706',
  colorOk: '#16a34a',

  nodeLabelBg: '#f5f3ff',
  nodeLabelBorder: '#ddd6fe',
  nodeLabelColor: '#a78bfa',
};

export const creamPalette: ThemePalette = {
  key: 'cream',
  labelKey: 'profile.themeCream',
  algorithm: 'light',
  colorPrimary: '#b45309',
  borderRadius: 8,

  siderBg: '#fff9f0',
  headerBg: '#fff9f0',
  bodyBg: '#faf6ef',
  menuDarkItemBg: '#fff9f0',
  menuDarkSubMenuItemBg: '#fff9f0',

  bgBase: '#faf6ef',
  bgSurface: '#fff9f0',
  bgElevated: '#ffffff',
  textPrimary: '#3b2f1e',
  textSecondary: '#7c5c38',
  textTertiary: '#b59a7d',
  borderColor: '#e8d5b7',
  borderColorLight: '#f0e4cc',
  scrollbarThumb: '#e8d5b7',
  scrollbarThumbHover: '#d4b896',

  loginGradientStart: '#faf6ef',
  loginGradientMid: '#f5ede0',
  loginGradientEnd: '#ecddc8',
  loginCardBg: 'rgba(255, 249, 240, 0.95)',

  treeHoverBg: '#f5ede0',
  treeSelectedBg: '#fde8cc',

  explorerItemHoverBg: '#f5ede0',
  explorerItemActiveBg: '#fde8cc',
  explorerItemActiveBorder: '#b45309',

  calendarTodayBg: '#fde8cc',
  calendarSelectedBg: '#b45309',

  colorOverdue: '#dc2626',
  colorUpcoming: '#d97706',
  colorOk: '#16a34a',

  nodeLabelBg: '#f5ede0',
  nodeLabelBorder: '#e8d5b7',
  nodeLabelColor: '#b59a7d',
};

export const midnightPalette: ThemePalette = {
  key: 'midnight',
  labelKey: 'profile.themeMidnight',
  algorithm: 'dark',
  colorPrimary: '#06b6d4',
  borderRadius: 8,

  siderBg: '#0d1117',
  headerBg: '#0d1117',
  bodyBg: '#080b0f',
  menuDarkItemBg: '#0d1117',
  menuDarkSubMenuItemBg: '#0d1117',

  bgBase: '#080b0f',
  bgSurface: '#0d1117',
  bgElevated: 'rgba(13, 17, 23, 0.95)',
  textPrimary: '#cdd9e5',
  textSecondary: '#768390',
  textTertiary: '#444c56',
  borderColor: '#21262d',
  borderColorLight: '#161b22',
  scrollbarThumb: '#21262d',
  scrollbarThumbHover: '#30363d',

  loginGradientStart: '#080b0f',
  loginGradientMid: '#0d1117',
  loginGradientEnd: '#0d1f2d',
  loginCardBg: 'rgba(13, 17, 23, 0.95)',

  treeHoverBg: '#111820',
  treeSelectedBg: '#0d2a3a',

  explorerItemHoverBg: '#111820',
  explorerItemActiveBg: '#0d2a3a',
  explorerItemActiveBorder: '#06b6d4',

  calendarTodayBg: '#0d2a3a',
  calendarSelectedBg: '#06b6d4',

  colorOverdue: '#f87171',
  colorUpcoming: '#fbbf24',
  colorOk: '#4ade80',

  nodeLabelBg: '#0d1117',
  nodeLabelBorder: '#21262d',
  nodeLabelColor: '#444c56',
};

export const oceanPalette: ThemePalette = {
  key: 'ocean',
  labelKey: 'profile.themeOcean',
  algorithm: 'dark',
  colorPrimary: '#0891b2',
  borderRadius: 8,

  siderBg: '#061a24',
  headerBg: '#061a24',
  bodyBg: '#040f14',
  menuDarkItemBg: '#061a24',
  menuDarkSubMenuItemBg: '#061a24',

  bgBase: '#040f14',
  bgSurface: '#061a24',
  bgElevated: 'rgba(6, 26, 36, 0.95)',
  textPrimary: '#e0f2fe',
  textSecondary: '#6ba3b0',
  textTertiary: '#2a5a66',
  borderColor: '#0e3a4a',
  borderColorLight: '#092d3c',
  scrollbarThumb: '#0e3a4a',
  scrollbarThumbHover: '#155e75',

  loginGradientStart: '#040f14',
  loginGradientMid: '#061a24',
  loginGradientEnd: '#0a2a3f',
  loginCardBg: 'rgba(6, 26, 36, 0.95)',

  treeHoverBg: '#07202e',
  treeSelectedBg: '#0c3a50',

  explorerItemHoverBg: '#07202e',
  explorerItemActiveBg: '#0c3a50',
  explorerItemActiveBorder: '#0891b2',

  calendarTodayBg: '#0c3a50',
  calendarSelectedBg: '#0891b2',

  colorOverdue: '#f87171',
  colorUpcoming: '#fbbf24',
  colorOk: '#4ade80',

  nodeLabelBg: '#061a24',
  nodeLabelBorder: '#0e3a4a',
  nodeLabelColor: '#2a5a66',
};

export const rosePalette: ThemePalette = {
  key: 'rose',
  labelKey: 'profile.themeRose',
  algorithm: 'light',
  colorPrimary: '#e11d48',
  borderRadius: 8,

  siderBg: '#fff5f6',
  headerBg: '#fff5f6',
  bodyBg: '#fff0f2',
  menuDarkItemBg: '#fff5f6',
  menuDarkSubMenuItemBg: '#fff5f6',

  bgBase: '#fff0f2',
  bgSurface: '#fff5f6',
  bgElevated: '#ffffff',
  textPrimary: '#3d0014',
  textSecondary: '#9f1239',
  textTertiary: '#fda4af',
  borderColor: '#fecdd3',
  borderColorLight: '#ffe4e6',
  scrollbarThumb: '#fecdd3',
  scrollbarThumbHover: '#fda4af',

  loginGradientStart: '#fff0f2',
  loginGradientMid: '#ffe4e6',
  loginGradientEnd: '#fecdd3',
  loginCardBg: 'rgba(255, 255, 255, 0.95)',

  treeHoverBg: '#ffe4e6',
  treeSelectedBg: '#fecdd3',

  explorerItemHoverBg: '#ffe4e6',
  explorerItemActiveBg: '#fecdd3',
  explorerItemActiveBorder: '#e11d48',

  calendarTodayBg: '#fecdd3',
  calendarSelectedBg: '#e11d48',

  colorOverdue: '#dc2626',
  colorUpcoming: '#d97706',
  colorOk: '#16a34a',

  nodeLabelBg: '#fff5f6',
  nodeLabelBorder: '#fecdd3',
  nodeLabelColor: '#fda4af',
};

export const carbonPalette: ThemePalette = {
  key: 'carbon',
  labelKey: 'profile.themeCarbon',
  algorithm: 'dark',
  colorPrimary: '#f97316',
  borderRadius: 8,

  siderBg: '#161616',
  headerBg: '#161616',
  bodyBg: '#0c0c0c',
  menuDarkItemBg: '#161616',
  menuDarkSubMenuItemBg: '#161616',

  bgBase: '#0c0c0c',
  bgSurface: '#161616',
  bgElevated: 'rgba(22, 22, 22, 0.95)',
  textPrimary: '#e5e5e5',
  textSecondary: '#a3a3a3',
  textTertiary: '#525252',
  borderColor: '#262626',
  borderColorLight: '#1f1f1f',
  scrollbarThumb: '#262626',
  scrollbarThumbHover: '#404040',

  loginGradientStart: '#0c0c0c',
  loginGradientMid: '#161616',
  loginGradientEnd: '#1a1209',
  loginCardBg: 'rgba(22, 22, 22, 0.95)',

  treeHoverBg: '#1a1a1a',
  treeSelectedBg: '#2d1a08',

  explorerItemHoverBg: '#1a1a1a',
  explorerItemActiveBg: '#2d1a08',
  explorerItemActiveBorder: '#f97316',

  calendarTodayBg: '#2d1a08',
  calendarSelectedBg: '#f97316',

  colorOverdue: '#ef4444',
  colorUpcoming: '#f97316',
  colorOk: '#22c55e',

  nodeLabelBg: '#161616',
  nodeLabelBorder: '#262626',
  nodeLabelColor: '#525252',
};

export const pinePalette: ThemePalette = {
  key: 'pine',
  labelKey: 'profile.themePine',
  algorithm: 'dark',
  colorPrimary: '#0d9488',
  borderRadius: 8,

  siderBg: '#061a19',
  headerBg: '#061a19',
  bodyBg: '#030f0e',
  menuDarkItemBg: '#061a19',
  menuDarkSubMenuItemBg: '#061a19',

  bgBase: '#030f0e',
  bgSurface: '#061a19',
  bgElevated: 'rgba(6, 26, 25, 0.95)',
  textPrimary: '#ccfbf1',
  textSecondary: '#6aaba3',
  textTertiary: '#2d6b66',
  borderColor: '#0d3d39',
  borderColorLight: '#092e2b',
  scrollbarThumb: '#0d3d39',
  scrollbarThumbHover: '#155e58',

  loginGradientStart: '#030f0e',
  loginGradientMid: '#061a19',
  loginGradientEnd: '#082c29',
  loginCardBg: 'rgba(6, 26, 25, 0.95)',

  treeHoverBg: '#071f1e',
  treeSelectedBg: '#0d3535',

  explorerItemHoverBg: '#071f1e',
  explorerItemActiveBg: '#0d3535',
  explorerItemActiveBorder: '#0d9488',

  calendarTodayBg: '#0d3535',
  calendarSelectedBg: '#0d9488',

  colorOverdue: '#f87171',
  colorUpcoming: '#fbbf24',
  colorOk: '#34d399',

  nodeLabelBg: '#061a19',
  nodeLabelBorder: '#0d3d39',
  nodeLabelColor: '#2d6b66',
};

export const palettes: ThemePalette[] = [
  darkPalette, lightPalette,
  navyPalette, forestPalette, amberPalette, lavenderPalette, creamPalette,
  midnightPalette, oceanPalette, rosePalette, carbonPalette, pinePalette,
];

/**
 * Apply a palette's CSS custom-properties to the document root.
 */
export function applyPaletteCSSVars(p: ThemePalette) {
  const root = document.documentElement;
  root.style.setProperty('--bg-base', p.bgBase);
  root.style.setProperty('--bg-surface', p.bgSurface);
  root.style.setProperty('--bg-elevated', p.bgElevated);
  root.style.setProperty('--text-primary', p.textPrimary);
  root.style.setProperty('--text-secondary', p.textSecondary);
  root.style.setProperty('--text-tertiary', p.textTertiary);
  root.style.setProperty('--border-color', p.borderColor);
  root.style.setProperty('--border-color-light', p.borderColorLight);
  root.style.setProperty('--scrollbar-thumb', p.scrollbarThumb);
  root.style.setProperty('--scrollbar-thumb-hover', p.scrollbarThumbHover);
  root.style.setProperty('--login-gradient-start', p.loginGradientStart);
  root.style.setProperty('--login-gradient-mid', p.loginGradientMid);
  root.style.setProperty('--login-gradient-end', p.loginGradientEnd);
  root.style.setProperty('--login-card-bg', p.loginCardBg);
  root.style.setProperty('--tree-hover-bg', p.treeHoverBg);
  root.style.setProperty('--tree-selected-bg', p.treeSelectedBg);
  root.style.setProperty('--explorer-item-hover-bg', p.explorerItemHoverBg);
  root.style.setProperty('--explorer-item-active-bg', p.explorerItemActiveBg);
  root.style.setProperty('--explorer-item-active-border', p.explorerItemActiveBorder);
  root.style.setProperty('--calendar-today-bg', p.calendarTodayBg);
  root.style.setProperty('--calendar-selected-bg', p.calendarSelectedBg);
  root.style.setProperty('--color-overdue', p.colorOverdue);
  root.style.setProperty('--color-upcoming', p.colorUpcoming);
  root.style.setProperty('--color-ok', p.colorOk);
  root.style.setProperty('--node-label-bg', p.nodeLabelBg);
  root.style.setProperty('--node-label-border', p.nodeLabelBorder);
  root.style.setProperty('--node-label-color', p.nodeLabelColor);
}
